import { AddressAutocomplete } from "@components/AddressAutocomplete";
import { FormCheckbox } from "@components/FormCheckbox";
import { FormDatePicker } from "@components/FormDatePicker";
import { FormInput } from "@components/FormInput";
import { FormMultiSelect } from "@components/FormMultiSelect";
import { FormSelect } from "@components/FormSelect";
import { RenderRolesWithAliases } from "./RenderRolesWithAliases";
import { Grid, InputAdornment, Typography } from "@mui/material";
import {
  ModelUserRole,
  ModelUserRoleAliasBan,
  useAdminDepartmentGet,
  useAdminJobTitleGet,
  useLookupCountryCountryIdSubdivisionGet
} from "@sportsgravyengineering/sg-api-react-sdk";
import { capitalize } from "@utils/capitalize";
import React, { useEffect } from "react";
import { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { HeaderUnderLine } from "@components/HeaderUnderLine";
import { FormTimePicker } from "@components/FormTimePicker";
import { EMPLOYMENT_STATUS_TYPES, EMPLOYMENT_TYPES } from "@utils/constants";
export const UserDetailsForm = ({
  disabled,
  form,
  country,
  selectAddressSuggestion,
  isEditing,
  countries,
  isFetchingCountries,
  organizationId,
  roles,
  roleOptions,
  hideBanPortion,
  bannedRolesOptions,
  onChangeBannedRoles,
  onBannedRolesRemoved,
  isUserBanned,
  isUnderAge,
  orgBannedRoles,
  sgBannedRoles,
  hideSGUserFields,
  onChangeRolesSelected
}: {
  disabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any, any, undefined>;
  country: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectAddressSuggestion: any;
  isEditing: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  countries: any;
  isFetchingCountries: boolean;
  organizationId: string | undefined;
  roles?: ModelUserRole[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  roleOptions?: any[];
  hideBanPortion?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bannedRolesOptions: any[];
  onChangeBannedRoles?: (event) => void;
  onBannedRolesRemoved?: (value) => void;
  onChangeRolesSelected?: (roleId) => void;
  isUserBanned?: boolean;
  isUnderAge?: boolean;
  orgBannedRoles?: string[];
  sgBannedRoles?: string[] | ModelUserRoleAliasBan[] | undefined;
  hideSGUserFields?: boolean;
}) => {
  const { control, setValue } = form;
  const { data: provinces } = useLookupCountryCountryIdSubdivisionGet(
    country as string,
    {
      query: {
        enabled: !!country
      }
    }
  );
  const timeZoneOptions = Intl.supportedValuesOf("timeZone").map((key) => {
    return {
      label: key,
      value: key
    };
  });
  const provincesOptions = useMemo(() => {
    if (provinces) {
      return provinces.data.map((province) => ({
        value: province.subdivisionId,
        label: province.name
      }));
    }
    return [];
  }, [provinces]);
  const countryOptions = useMemo(() => {
    if (countries) {
      return countries.map((country) => ({
        value: country.countryId,
        label: country.name
      }));
    }
    return [];
  }, [countries]);

  const selectedCountry = useMemo(() => {
    if (countries?.length) {
      return countries?.find((c) => c.countryId === country);
    }
    return null;
  }, [country, countries]);

  const [isBannedUser, setIsBannedUser] = React.useState(isUserBanned);

  useEffect(() => {
    setIsBannedUser(isUserBanned);
  }, [isUserBanned]);

  const [departments, setDepartments] = React.useState<
    { label: string; value: string }[]
  >([]);

  const { data: departmentsData, isLoading: isLoadingDepatrtments } =
    useAdminDepartmentGet();

  useEffect(() => {
    if (departmentsData?.data) {
      setDepartments(
        departmentsData.data.departments?.map((dep) => {
          return {
            label: dep.name!,
            value: dep.departmentId!
          };
        }) || []
      );
    }
  }, [departmentsData]);

  const [jobTitles, setJobTitles] = React.useState<
    { label: string; value: string }[]
  >([]);

  const { data: jobTitlesData, isLoading: isLoadingJobTitles } =
    useAdminJobTitleGet();

  useEffect(() => {
    if (jobTitlesData?.data) {
      setJobTitles(
        jobTitlesData.data.jobTitles?.map((jt) => {
          return {
            label: jt.name!,
            value: jt.jobtitleId!
          };
        }) || []
      );
    }
  }, [jobTitlesData]);

  const suffixOptions = [
    { value: "", label: "" },
    { value: "Jr", label: "Jr" },
    { value: "Sr", label: "Sr" },
    { value: "I", label: "I" },
    { value: "II", label: "II" },
    { value: "III", label: "III" },
    { value: "IV", label: "IV" },
    { value: "V", label: "V" }
  ];

  const openRoleDesc = (roleId) => {
    window.open("/roles/" + roleId, "_blank");
  };

  const getOrgRoles = () => {
    const organizationRolesMap = roles!.reduce((map, role) => {
      const roleOrganizationId = role?.role?.organization?.organizationId
        ? role.role.organization.organizationId
        : "0";
      if (!map[roleOrganizationId]) {
        map[roleOrganizationId] = [];
      }
      if (
        role?.role?.alias !== "SPORTSFAN" &&
        ((roleOrganizationId === "0" && role?.role?.type === "SYSTEM") ||
          (roleOrganizationId !== "0" && role?.role?.type === "ORGANIZATION"))
      ) {
        if (isEditing) {
          if (roleOrganizationId === "0" && !organizationId)
            map[roleOrganizationId].push(role);
        } else {
          map[roleOrganizationId].push(role);
        }
      }
      return map;
    }, {});
    return organizationRolesMap;
  };

  const getBannedRolesSelected = (orgRoles) => {
    const bannedRoles = orgRoles.filter((role) => role.isBanned === false);
    if (!isEditing && bannedRoles.length === orgRoles.length) return ["ALL"];
    const bannedRolesValues = bannedRoles.map((role) => role.role.alias);
    const uniqueBannedRolesValues: string[] = [];
    const seenValues = new Set();

    for (const role of bannedRolesValues) {
      if (!seenValues.has(role.value)) {
        seenValues.add(role.value);
        uniqueBannedRolesValues.push(role);
      }
    }
    return uniqueBannedRolesValues;
  };
  const getBannedRolesOptions = (orgRoles) => {
    const bannedRolesOptions = orgRoles.map((role) => ({
      label: capitalize(role.role.alias),
      value: role.role.alias
    }));
    if (!isEditing)
      return [...bannedRolesOptions, { label: "All Roles", value: "ALL" }];
    const uniqueBannedRolesOptions: { label: string; value: string }[] = [];
    const seenValues = new Set();

    for (const role of bannedRolesOptions) {
      if (!seenValues.has(role.value)) {
        seenValues.add(role.value);
        uniqueBannedRolesOptions.push(role);
      }
    }
    return uniqueBannedRolesOptions;
  };

  const renderOrgRoles = (type) => {
    const organizationRolesMap = getOrgRoles();
    if (type === "ROLES") {
      return (
        <>
          {Object.keys(organizationRolesMap).map(
            (orgId, index) =>
              organizationRolesMap[orgId].length > 0 && (
                <Grid
                  item
                  container
                  direction="column"
                  spacing="24px"
                  key={orgId}
                >
                  <Grid item>
                    {index > 0 && <br />}
                    <Typography data-testid={"ROLE_ORGNAME_" + orgId}>
                      For{" "}
                      {orgId === "0"
                        ? " SportsGravy"
                        : organizationRolesMap[orgId][0].role.organization.name}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <RenderRolesWithAliases
                      orgRoles={organizationRolesMap[orgId]}
                      control={control}
                      isEditable={isEditing}
                      organizationId={organizationId}
                      disabled={disabled}
                    />
                  </Grid>
                </Grid>
              )
          )}
        </>
      );
    } else if (type === "BAN") {
      if (!isEditing && !organizationId) {
        return (
          <Grid
            item
            container
            direction="column"
            spacing="24px"
            data-testid="UserDetailsBannedRolesDropdown"
          >
            <Grid item container direction="column" spacing="24px">
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={12}>
                  <FormMultiSelect
                    name={"bannedRolesSG"}
                    value={"bannedRolesSG"}
                    control={control}
                    required
                    label="Banned from SportsGravy for the following roles"
                    options={bannedRolesOptions}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
            {Object.keys(organizationRolesMap)
              .filter((key) => key != "0")
              .map((orgId) => {
                const bannedRolesSelected = getBannedRolesSelected(
                  organizationRolesMap[orgId]
                );
                if (bannedRolesSelected.length)
                  return (
                    <Grid
                      item
                      container
                      direction="column"
                      spacing="24px"
                      key={orgId}
                    >
                      <Grid item container direction="row" spacing="24px">
                        <Grid item xs={12}>
                          <FormMultiSelect
                            name="rolesBanned"
                            control={control}
                            preSelectedOptions={bannedRolesSelected}
                            required={orgId === "0"}
                            label={
                              "Banned from " +
                              organizationRolesMap[orgId][0].role.organization
                                .name +
                              " for the following roles"
                            }
                            options={getBannedRolesOptions(
                              organizationRolesMap[orgId]
                            )}
                            disabled
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  );
              })}
          </Grid>
        );
      } else {
        return (
          <Grid
            item
            container
            direction="column"
            spacing="24px"
            data-testid="UserDetailsBannedRolesDropdown"
          >
            <Grid item container direction="column" spacing="24px">
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={12}>
                  <FormMultiSelect
                    name={!organizationId ? "bannedRolesSG" : "bannedRoles"}
                    value={!organizationId ? "bannedRolesSG" : "bannedRoles"}
                    control={control}
                    required
                    label="For the following roles"
                    options={bannedRolesOptions}
                    disabled={disabled}
                    onChange={onChangeBannedRoles}
                    onRemove={onBannedRolesRemoved}
                  />
                </Grid>
              </Grid>

              {isEditing && (
                <Grid item xs={6} data-testid="USER_DETAILS_BAN_REASON">
                  <FormInput
                    name="bannedReason"
                    sideMessage="User will not be notified"
                    control={control}
                    rules={{ required: "Reason is required" }}
                    label="Reason"
                    type="text"
                    required={true}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        );
      }
    }
  };

  return (
    <Grid
      container
      direction="column"
      spacing="25px"
      data-testid="userDetailsForm"
    >
      <Grid item container direction="row" spacing="24px">
        <Grid item xs={12} data-testid="UserDetailsAvatar">
          <FormInput
            name="photoUrl"
            control={control}
            label="Avatar"
            type="avatar"
            disabled
          />
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing="24px">
        <Grid item xs={3}>
          <FormInput
            name="firstName"
            control={control}
            rules={{ required: "First Name is required" }}
            label="First Name"
            type="text"
            required={true}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <FormInput
            name="middleName"
            control={control}
            label="Middle Name"
            type="text"
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <FormInput
            name="lastName"
            control={control}
            rules={{ required: "Last Name is required" }}
            label="Last Name"
            type="text"
            required={true}
            disabled
          />
        </Grid>
        <Grid item xs={3}>
          <FormSelect
            name="suffix"
            control={control}
            label="Suffix"
            options={suffixOptions}
            disabled
          />
        </Grid>
      </Grid>
      {organizationId && (
        <Grid item container direction="row" spacing="24px">
          <Grid item xs={6}>
            <FormInput
              name="phone"
              type="tel"
              country={selectedCountry}
              required
              label="Mobile Phone"
              control={control}
              rules={{
                required: "Mobile Phone is required"
              }}
              disabled
            />
          </Grid>
          {isUnderAge && (
            <Grid item xs={6}>
              <FormDatePicker
                name="birthedAt"
                required
                control={control}
                label="Date of Birth"
                disabled
              />
            </Grid>
          )}
        </Grid>
      )}
      {!organizationId && (
        <Grid item container direction="column" spacing="25px">
          <Grid item width="50%">
            <FormDatePicker
              name="birthedAt"
              required
              control={control}
              label="Date of Birth"
              disabled
            />
          </Grid>
          <Grid item xs={12}>
            <Typography
              variant="permissionNames"
              sx={{
                fontWeight: 400,
                fontSize: "12px",
                letterSpacing: "10%",
                lineHeight: "14.52px"
              }}
            >
              CONTACT DETAILS
            </Typography>
            <HeaderUnderLine />
          </Grid>
          <Grid item container direction="row" spacing="24px">
            <Grid item container direction="column" xs={6} spacing="10px">
              <Grid item>
                <FormInput
                  name="email"
                  control={control}
                  rules={{ required: "Email is required" }}
                  label="Email"
                  type="email"
                  required={true}
                  disabled
                />
              </Grid>
              <Grid item>
                <Typography
                  variant="caption"
                  style={{
                    color: "#B3B3B3"
                  }}
                >
                  Note: This data point is controlled by the user, which is why
                  it's not editable.
                </Typography>
              </Grid>
            </Grid>
            <Grid item container direction="column" xs={6} spacing="10px">
              <Grid item data-testid="sgPhoneInput">
                <FormInput
                  name="phone"
                  type="tel"
                  country={selectedCountry}
                  required
                  label="Mobile Phone"
                  control={control}
                  rules={{
                    required: "Mobile Phone is required"
                  }}
                  disabled
                />
              </Grid>
              <Grid item>
                <Typography
                  variant="caption"
                  style={{
                    color: "#B3B3B3"
                  }}
                >
                  Note: This data point is controlled by the user, which is why
                  it's not editable.
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {!hideSGUserFields && (
            <Grid
              item
              container
              direction="column"
              spacing="25px"
              data-testid="sgUserFields"
            >
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={6}>
                  <FormInput
                    name="sportsgravyUser.skypeId"
                    control={control}
                    label="Skype ID"
                    type="text"
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    name="sportsgravyUser.whatsappNumber"
                    type="tel"
                    country={selectedCountry}
                    label="Whatsapp Number"
                    control={control}
                    disabled={!isEditing}
                  />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="permissionNames"
                  sx={{
                    fontWeight: 400,
                    fontSize: "12px",
                    letterSpacing: "10%",
                    lineHeight: "14.52px"
                  }}
                >
                  JOB DETAILS
                </Typography>
                <HeaderUnderLine />
              </Grid>
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={6} container direction="column" spacing="20px">
                  <Grid item>
                    <FormSelect
                      name="sportsgravyUser.jobTitleId"
                      control={control}
                      label="Job Title"
                      options={jobTitles}
                      isLoading={isLoadingJobTitles}
                      onChange={(e) => {
                        const jobTitle =
                          jobTitlesData?.data?.jobTitles?.find(
                            (jt) => jt.jobtitleId === e.target.value
                          ) || undefined;
                        if (jobTitle) {
                          setValue(
                            "sportsgravyUser.departmentId",
                            jobTitle.departmentId,
                            {
                              shouldValidate: true
                            }
                          );
                        }
                      }}
                      required
                      rules={{
                        required: "Job Title is required"
                      }}
                      disabled={!isEditing}
                    />
                  </Grid>
                  <Grid item>
                    <FormCheckbox
                      control={control}
                      name="sportsgravyUser.isAvailableSalesDemo"
                      label="Available For Sales Demo"
                      labelPadding="4px 0"
                      labelTypographyProps={{
                        variant: "body1"
                      }}
                      disabled={!isEditing}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={6}>
                  <FormSelect
                    name="sportsgravyUser.departmentId"
                    control={control}
                    label="Department"
                    options={departments}
                    isLoading={isLoadingDepatrtments}
                    required
                    disabled
                  />
                </Grid>
              </Grid>
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={6}>
                  <FormDatePicker
                    name="sportsgravyUser.startDate"
                    control={control}
                    label="Start Date"
                    required
                    rules={{
                      required: "Start Date is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    name="sportsgravyUser.yearsOfService"
                    control={control}
                    label="Years of Service"
                    type="number"
                    required
                    rules={{
                      required: "Years of Service is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
              </Grid>
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={3}>
                  <FormTimePicker
                    name="sportsgravyUser.startTime"
                    control={control}
                    label="Start Time"
                    required
                    rules={{
                      required: "Start Time is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormTimePicker
                    name="sportsgravyUser.endTime"
                    control={control}
                    label="End Time"
                    required
                    rules={{
                      required: "End Time is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormSelect
                    control={control}
                    required
                    name="sportsgravyUser.timeZone"
                    label="Time Zone"
                    disabled={!isEditing}
                    options={timeZoneOptions}
                    rules={{
                      required: "Time Zone is required"
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={3}>
                  <FormSelect
                    name="sportsgravyUser.employmentType"
                    control={control}
                    label="Employment Type"
                    options={EMPLOYMENT_TYPES}
                    required
                    rules={{
                      required: "Employment Type is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormSelect
                    name="sportsgravyUser.employmentStatus"
                    control={control}
                    label="Employment Status"
                    options={EMPLOYMENT_STATUS_TYPES}
                    required
                    rules={{
                      required: "Employment Status is required"
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormInput
                    control={control}
                    required
                    name="sportsgravyUser.jiraAccountId"
                    type="text"
                    label="Jira Id"
                    disabled={!isEditing}
                    rules={{
                      required: "Jira Id is required"
                    }}
                  />
                </Grid>
              </Grid>
              <Grid item container direction="row" spacing="24px">
                <Grid item xs={6}>
                  <FormInput
                    name="sportsgravyUser.hourlyRate"
                    control={control}
                    rules={{ required: "Hourly rate is required" }}
                    label="Hourly Rate"
                    type="text"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="start">USD</InputAdornment>
                      )
                    }}
                    required={true}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    name="sportsgravyUser.avgHoursPerMonth"
                    control={control}
                    type="number"
                    label="Avg Hours per Month"
                    required
                    rules={{
                      required: "Avg Hours per Month is required"
                    }}
                    onChange={(e) => {
                      if (e?.target?.value && !isNaN(e.target.value)) {
                        const hpm = parseInt(e.target.value);
                        if (hpm) {
                          setValue("sportsGravyUserAvgHoursPerWeek", hpm / 4);
                        }
                      }
                    }}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormInput
                    name="sportsGravyUserAvgHoursPerWeek"
                    control={control}
                    type="number"
                    label="Avg Hours per Week"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
      {organizationId && (
        <Grid item container direction="row" spacing="24px">
          <Grid item xs={6}>
            <FormCheckbox
              key="isUnderAge"
              control={control}
              name="isUnderAge"
              label="This person is below 18 years of age"
              labelPadding="4px 0"
              labelTypographyProps={{
                variant: "body1"
              }}
              disabled
            />
          </Grid>
        </Grid>
      )}
      {/* All users by default have sports fan role, hence roles.length has to be more than 1 for roles to be displayed */}
      {!isEditing && !organizationId && roles && roles.length > 1 && (
        <>
          <Grid item xs={6}>
            <Typography
              variant="permissionNames"
              sx={{
                color: "#00000",
                fontSize: "12px",
                font: "inter",
                lineHeight: "14.52px",
                letterSpacing: "10%",
                fontWeight: 400
              }}
            >
              ROLES & SUB ROLES
            </Typography>
          </Grid>
          {renderOrgRoles("ROLES")}
        </>
      )}

      {!hideBanPortion && (
        <>
          <Grid item xs={6}>
            <Typography
              variant="permissionNames"
              sx={{
                color: "#00000",
                fontSize: "12px",
                font: "inter",
                lineHeight: "14.52px",
                letterSpacing: "10%",
                fontWeight: 400
              }}
            >
              BAN
            </Typography>
            <HeaderUnderLine />
          </Grid>
          <Grid
            item
            container
            direction="row"
            spacing="24px"
            style={{ marginLeft: "-1px" }}
          >
            <Grid
              xs={6}
              style={{ marginTop: "10px" }}
              data-testid={"userDetailsIsBanned"}
            >
              <FormCheckbox
                key="bannedCheckBox"
                control={control}
                name="isBanned"
                onChange={() => {
                  setValue("isBanned", !isBannedUser);
                  setIsBannedUser(!isBannedUser);
                }}
                label={
                  !organizationId
                    ? isEditing
                      ? "Ban this user from SportsGravy"
                      : "Banned from SportsGravy"
                    : isEditing
                    ? "Ban this user from the Organization"
                    : "Banned from Organization"
                }
                labelPadding="4px 8px"
                labelTypographyProps={{
                  variant: "body1"
                }}
                disabled={!isEditing}
              />
            </Grid>
          </Grid>
        </>
      )}

      {!hideBanPortion && isBannedUser && renderOrgRoles("BAN")}
      {(isEditing || organizationId) && roleOptions && roleOptions.length && (
        <>
          <Grid item xs={6}>
            <Typography
              variant="permissionNames"
              sx={{
                color: "#00000",
                fontSize: "12px",
                font: "inter",
                lineHeight: "14.52px",
                letterSpacing: "10%",
                fontWeight: 400
              }}
            >
              ROLES & SUB ROLES
            </Typography>
            <HeaderUnderLine />
          </Grid>
          <RenderRolesWithAliases
            orgRoles={roleOptions}
            control={control}
            isEditable={isEditing}
            organizationId={organizationId}
            disabled={disabled}
            onChangeRolesSelected={onChangeRolesSelected}
            openRoleDesc={openRoleDesc}
            sgBannedRoles={sgBannedRoles}
            orgBannedRoles={orgBannedRoles}
          />
        </>
      )}

      <Grid item container direction="row" spacing="24px">
        <Grid item xs={6}>
          <Typography
            variant="permissionNames"
            sx={{
              color: "#00000",
              fontSize: "12px",
              font: "inter",
              lineHeight: "14.52px",
              letterSpacing: "10%",
              fontWeight: 400
            }}
          >
            ADDRESS DETAILS
          </Typography>
        </Grid>
      </Grid>
      <HeaderUnderLine marginLeft="21px" />
      <Grid item container direction="row" spacing="24px">
        <Grid item xs={6}>
          <FormSelect
            name="country"
            control={control}
            rules={{ required: "Country is required" }}
            options={countryOptions}
            isLoading={isFetchingCountries}
            label="Country"
            required
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <AddressAutocomplete
            name="address1"
            control={control}
            selectSuggestion={selectAddressSuggestion}
            rules={{
              required: "Address is required"
            }}
            label="Address Line 1"
            required
            disabled
          />
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing="24px">
        <Grid item xs={6}>
          <FormInput
            name="address2"
            control={control}
            label="Address Line 2"
            type="text"
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            name="locality"
            control={control}
            rules={{ required: "City is required" }}
            label="City"
            type="text"
            required
            disabled
          />
        </Grid>
      </Grid>
      <Grid item container direction="row" spacing="24px">
        <Grid item xs={6}>
          <FormSelect
            name="province"
            control={control}
            rules={{ required: "State/Province is required" }}
            label="State/Province"
            options={provincesOptions}
            type="text"
            required
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <FormInput
            name="postalCode"
            control={control}
            rules={{ required: "Zip is required" }}
            label="Zip Code"
            type="text"
            required
            disabled
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

UserDetailsForm.defaultProps = {
  countries: [],
  isFetchingCountries: false,
  hideBanPortion: false,
  isUnderAge: false
};
